import {
  getFigureTotalHeight,
  isConsecutiveAsideImage,
  isValidTag,
  placeAsideContainer,
} from './asides';

/**
 * Checks if any element in the array has at least one of the specified class names.
 *
 * @param elements - An array of HTML elements to check.
 * @param classNames - An array of class names to look for.
 */
export const hasElementWithClass = (
  elements: HTMLElement[],
  classNames: string[],
): boolean => {
  return elements.some(element =>
    classNames.some(className => element.classList.contains(className)),
  );
};

/**
 * Collects valid sibling elements of an element based on its height.
 *
 * Iterates through the next siblings of the provided element, collecting
 * valid siblings based on tag types and height constraints. Handles special
 * cases for consecutive aside images and aside elements within siblings.
 *
 * @param element - The element to position aside.
 * @returns An array of valid sibling elements.
 */
export const collectSiblings = (
  element: HTMLElement,
  articleFirstElement: HTMLElement,
): HTMLElement[] => {
  const heightToCover = element.clientHeight; // Initial height to be covered by element's siblings
  let remainingHeight = heightToCover; // Remaining height to be covered, initialized as the element's height

  let next = articleFirstElement; // Start with the first element on article text
  let tempSiblings: HTMLElement[] = []; // Temporary collection of siblings
  let siblings: HTMLElement[] = []; // Final collection of valid siblings

  while (next) {
    // Skip consecutive aside images
    if (isConsecutiveAsideImage(next)) {
      next = next.nextElementSibling as HTMLElement;
      continue;
    }

    // Check if the next element is a valid tag and not a parallax text or embed block
    if (
      isValidTag(next, ['h2', 'p', 'ol', 'ul']) &&
      !(
        next.classList.contains('parallax-text') ||
        next.classList.contains('embed-block')
      )
    ) {
      tempSiblings.push(next); // Collect sibling
      remainingHeight -= next.clientHeight; // Reduce remaining height to cover

      // Check if element's height was covered
      if (remainingHeight <= 0) {
        if (hasElementWithClass(tempSiblings, ['bv-grid-float__item'])) {
          // If an Aside Image is found among collected siblings, reset collection
          siblings = [];
          tempSiblings = [];
          remainingHeight = heightToCover;
        } else {
          // If no aside image found, finalize sibling collection
          siblings.push(...tempSiblings);
          break;
        }
      }
      // Handle case when the next element is an Aside Image
    } else if (
      next.classList.contains('bv-grid-float__item') &&
      !next.nextElementSibling?.classList.contains('bv-grid-float__item')
    ) {
      // Reset sibling collection to only this aside element
      tempSiblings = [next];
      // Adjust height to cover based on the figure's total height,
      // to be able to also collect (and thus skip) the figure's wrapped siblings
      remainingHeight = getFigureTotalHeight(next);
    } else {
      // For invalid elements: reset collection and height to initial height, continue loop
      tempSiblings = [];
      remainingHeight = heightToCover;
    }

    // Move on to the next sibling element
    next = next.nextElementSibling as HTMLElement;
  }

  return siblings;
};

/**
 * Positions the Aside Reco Teaser element in the DOM.
 *
 * Finds the reco-teaser element by its data-id, collects its valid siblings,
 * and places it in the correct position relative to those siblings.
 */
export const positionRecoTeaserAside = (
  recoTeaser: HTMLElement,
  articleFirstElement: HTMLElement,
): void => {
  const siblings = collectSiblings(recoTeaser, articleFirstElement);

  // If no valid siblings are found,
  // center the reco-teaser as first element on article text
  if (siblings.length <= 0) {
    recoTeaser.classList.add('bv-grid-center__item');
    recoTeaser.classList.add('bv-grid-center__item--reco-teaser');

    articleFirstElement.parentNode?.insertBefore(
      recoTeaser,
      articleFirstElement,
    );

    return;
  }

  // Place reco-teaser aside based on collected siblings
  recoTeaser.classList.add('bv-grid-float__item');
  recoTeaser.classList.add('bv-grid-float__item--reco-teaser');

  placeAsideContainer(recoTeaser, 'right', siblings);
};
